"use client"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { useProductCardContext } from "@/app/(site)/_components/Organisms/Cards/ProductCards/ProductCard/ProductCardContext"
import ProductCardThumbnails from "@/app/(site)/_components/Organisms/Cards/ProductCards/ProductCard/ProductCardThumbnails"
import { type RelatedVariant } from "@/lib/content-service/types/ContentServiceProduct"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { FOCUS_VISIBLE_INSET_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"

type ProductCardDetailsThumbnailTypeProps = {
    allVariants: RelatedVariant[]
    langCode: string
    uri?: string
    variantLinkLabel: string
    name: string
    price?: string
    priceBeforeDiscount: string
    showAsOnSale: boolean
    showBenefits?: boolean
    listName?: string
    listId?: string
    listType?: string
    listHierarchy?: string
    isAboveTheFold: boolean
}

export default function ProductCardDetailsThumbnailType({
    allVariants,
    langCode,
    uri,
    variantLinkLabel,
    name,
    price,
    priceBeforeDiscount,
    showAsOnSale,
    showBenefits,
    listName,
    listId,
    listType,
    listHierarchy,
    isAboveTheFold
}: ProductCardDetailsThumbnailTypeProps) {
    const siteContext = useSiteContext()
    const { handleChangeVariant } = useProductCardContext()

    // Helper function to track color swatch clicks properly
    const handleGoToSwatchTracking = (swatch: RelatedVariant, swatchIndex: number) => {
        const trackingData: RelatedVariant = {
            ...swatch,
            // Ensure we have the required properties for tracking
            sku: swatch.sku || swatch.product || "unknown",
            name: swatch.name || "Unknown",
            priceAsNumber: swatch.priceAsNumber || 0,
            price: swatch.price || "",
            available: swatch.available || false,
            color_swatch: swatch.color_swatch || undefined
        }

        import("@/lib/utils/tracking/gtmEvents").then(({ selectItemEvent }) => {
            selectItemEvent(
                trackingData,
                { size: undefined, index: swatchIndex },
                listName || "Product List",
                listId || "plp",
                siteContext,
                listType || "plp",
                listHierarchy
            )
        })
    }

    return (
        <div
            onMouseLeave={() => handleChangeVariant(undefined)}
            className="grid grid-cols-12 overflow-hidden"
        >
            <div className={"col-span-12 flex flex-col gap-8 lg:col-span-12"}>
                <DynamicLink
                    className={clsx("flex flex-col gap-4", FOCUS_VISIBLE_INSET_CLASS)}
                    href={`/product/${uri}`}
                    prefetch={false}
                    aria-label={variantLinkLabel}
                >
                    <p className="min-w-0 Trim_Mono_POC_Text_12_130_6_500_uppercase sm:Trim_Mono_POC_Text_14_130_0_500_uppercase lg:Trim_Mono_POC_Text_15_130_7_500_uppercase">
                        {name}
                    </p>

                    <p className="flex w-full min-w-0 flex-wrap items-baseline gap-x-8 text-secondary-text Trim_Mono_POC_Text_12_130_0_400_uppercase sm:Trim_Mono_POC_Text_14_130_1_400_uppercase">
                        {showAsOnSale && (
                            <span className="shrink-0 whitespace-nowrap line-through">
                                {priceBeforeDiscount}
                            </span>
                        )}
                        <span className="shrink-0 whitespace-nowrap">{price}</span>
                    </p>
                </DynamicLink>

                <ProductCardThumbnails
                    allVariants={allVariants}
                    langCode={langCode}
                    showBenefits={showBenefits}
                    isAboveTheFold={isAboveTheFold}
                    handleGoToSwatchTracking={handleGoToSwatchTracking}
                />
            </div>
        </div>
    )
}
